"use client";

import { Mail, MapPin, Phone, Navigation } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FindTheGarden() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const hours = [{
    day: "MON",
    time: "CLOSED",
    today: false
  }, {
    day: "TUE",
    time: "8AM — 6PM",
    today: false
  }, {
    day: "WED",
    time: "8AM — 6PM",
    today: false
  }, {
    day: "THU",
    time: "8AM — 6PM",
    today: false
  }, {
    day: "FRI",
    time: "8AM — 8PM",
    today: true
  }, {
    day: "SAT",
    time: "7AM — 8PM",
    today: false
  }, {
    day: "SUN",
    time: "9AM — 5PM",
    today: false
  }];
  return <section data-section-id="349" id="find-the-garden" ref={ref} className="bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen lg:min-h-[720px]">
        {}
        <div data-index={0} className={`relative h-[400px] lg:h-auto border-b lg:border-b-0 lg:border-r border-border transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-6"}`}>
          <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701353/img/garden_map.png')] bg-cover bg-center opacity-90">
            <div className="absolute inset-0 bg-gradient-to-br from-background/40 via-transparent to-background/60" />
          </div>

          {}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <span className="absolute inset-0 rounded-lg bg-primary/40 animate-ping" />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-lg bg-primary shadow-2xl border-2 border-background">
                <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-primary-foreground" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                  <path d="M12 8v8" />
                  <path d="M8 12h8" />
                </svg>
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap">
                <div className="bg-background/90 backdrop-blur-sm px-3 py-1 border border-primary/40">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-primary">
                    ECO GROW
                  </span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="absolute top-6 left-6 font-mono text-[10px] tracking-[0.25em] text-foreground/60">
            N ↑
          </div>
          <div className="absolute bottom-6 left-6 font-mono text-[10px] tracking-[0.25em] text-foreground/60">
            SCALE · 1:2000
          </div>
          <div className="absolute bottom-6 right-6 font-mono text-[10px] tracking-[0.25em] text-foreground/60">
            COMMUNITY PLOTS
          </div>
        </div>

        {}
        <div data-index={1} className={`relative bg-background px-6 sm:px-10 lg:px-16 py-12 lg:py-16 flex flex-col justify-center transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
          {}
          <div className="hidden lg:block absolute right-4 top-1/2 -translate-y-1/2 rotate-90 origin-center">
            
          </div>

          <div className="max-w-xl">
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-xs tracking-[0.3em] text-primary">
                ——
              </span>
              <span className="font-mono text-xs tracking-[0.3em] text-muted-foreground">VISIT US</span>
            </div>

            {}
            <h2 className="font-serif font-default leading-[0.9] tracking-tight mb-10">
              <span className="block text-7xl sm:text-8xl lg:text-[120px] xl:text-[140px] text-foreground">
                GROW
              </span>
              <span className="block text-7xl sm:text-8xl lg:text-[120px] xl:text-[140px] text-primary relative">
                HERE.
                <svg viewBox="0 0 80 40" className="inline-block ml-3 h-6 w-12 text-primary -translate-y-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 25 Q 25 5, 60 20" />
                  <path d="M50 12 L 62 20 L 52 28" />
                </svg>
              </span>
            </h2>

            {}
            <div className="mb-8">
              <p className="font-serif text-xl sm:text-2xl text-foreground leading-snug">
                42 Garden Lane
                <br />
                Green Valley District
              </p>
            </div>

            {}
            <div className="flex items-center gap-4 mb-8">
              <Separator className="flex-1 bg-border" />
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 text-primary shrink-0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                <path d="M12 8v8" />
                <path d="M8 12h8" />
              </svg>
              <Separator className="flex-1 bg-border" />
            </div>

            {}
            <div className="mb-8">
              <div className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground mb-3">
                GARDEN HOURS
              </div>
              <div className="flex flex-col">
                {hours.map((row, i) => <div key={i} data-index={i} className={`flex items-center justify-between py-2.5 border-b border-border/60 last:border-b-0 ${row.today ? "bg-primary text-primary-foreground px-3 -mx-3 border-primary" : ""}`}>
                    <div className="flex items-center gap-3">
                      {row.today && <span className="font-mono text-[10px] tracking-[0.25em] font-bold">
                          TODAY ·
                        </span>}
                      <span className="font-mono text-sm tracking-[0.15em]">
                        {row.day}
                      </span>
                    </div>
                    <span className="font-mono text-sm tracking-[0.1em]">
                      {row.time}
                    </span>
                  </div>)}
              </div>
            </div>

            {}
            <div className="flex flex-col gap-3 mb-10">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-primary shrink-0" />
                <Link to="mailto:hello@ecogrow.org" className="font-mono text-sm text-foreground hover:text-primary transition-colors tracking-[0.05em]">
                  hello@ecogrow.org
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <Link to="tel:5551234567" className="font-mono text-sm text-foreground hover:text-primary transition-colors tracking-[0.05em]">
                  (555) 123-4567
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-primary shrink-0" />
                <span className="font-mono text-sm text-foreground tracking-[0.05em]">
                  Green Valley, City
                </span>
              </div>
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.25em] h-12 px-7 rounded-md">
                <Link to="https://www.google.com/maps/dir/?api=1&destination=42+Garden+Lane" newTab>
                  GET DIRECTIONS
                  <Navigation className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-border text-foreground hover:bg-accent hover:text-accent-foreground font-mono text-xs tracking-[0.25em] h-12 px-7 rounded-md">
                <Link to="tel:5551234567">
                  <Phone className="h-4 w-4" />
                  CONTACT US
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}